import EventEmitter from 'eventemitter3';
import Game from '../../Game';
import IRenderer from './IRenderer';
import GameObject from '../../GameObject/GameObject';
import { SceneEvent } from '../../Scene/Scene';
import System from '../System';
import { IGameState } from '../../State/Game/IGameState';
import { debounce } from 'Utility/async';

export enum RenderSystemEvent {
    SORT_CHILDREN = 'sort_children',
    BRING_TO_FRONT = 'bring_to_front',
    RESIZE = 'resize',
}

export default class RenderSystem extends System {
    public static renderUpdate(): void {
        RenderSystem.driver.update();
    }

    public static emit(event: string, ...args: any[]): void {
        this.emitter.emit(event, ...args);
    }

    public static off(
        event: string,
        fn?: (...args: any[]) => any,
        context?: any
    ): void {
        this.emitter.off(event, fn, context);
    }

    public static on(
        event: string,
        fn: (...args: any[]) => any,
        context?: any
    ): void {
        this.emitter.on(event, fn, context);
    }

    public static init() {
        // Pull renderer driver from game config.
        RenderSystem.driver = new Game.config.renderer();
        RenderSystem.driver.start();

        RenderSystem.onResize();
        const handleResize = debounce(RenderSystem.onResize, 200);
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
    }

    private static emitter: EventEmitter = new EventEmitter();
    private static driver: IRenderer;
    private static currentWidth: number;
    private static currentHeight: number;
    private static resizeTimeout: any;

    private static onResize(): void {
        RenderSystem.handleSizeChanged();

        // Avoid to call resize function multiple times at the same time.
        if (RenderSystem.resizeTimeout) {
            Game.ticker.clearTimeout(RenderSystem.resizeTimeout);
            RenderSystem.resizeTimeout = null;
        }

        // Call resize function again some time later to make sure that the size is correct.
        // This helps with an iPad issue.
        RenderSystem.resizeTimeout = Game.ticker.setTimeout(() => {
            RenderSystem.handleSizeChanged();
            RenderSystem.resizeTimeout = null;
        }, 1000);
    }

    private static handleSizeChanged(): void {
        const innerWidth: number = window.innerWidth;
        const innerHeight: number = window.innerHeight;

        if (
            RenderSystem.currentWidth !== innerWidth ||
            RenderSystem.currentHeight !== innerHeight
        ) {
            RenderSystem.currentWidth = innerWidth;
            RenderSystem.currentHeight = innerHeight;
            RenderSystem.driver.resize(window.innerWidth, window.innerHeight);
            RenderSystem.emit(RenderSystemEvent.RESIZE);
        }
    }

    private static onBringToFront(gameObject: GameObject): void {
        RenderSystem.driver.bringToFront(gameObject);
    }

    private static sortChildren(gameObject: GameObject): void {
        RenderSystem.driver.sortChildren(gameObject);
    }

    private static gameObjectAdded(
        gameObject: GameObject<PIXI.Container>
    ): void {
        RenderSystem.driver.addGameObject(gameObject);
    }

    private static gameObjectRemoved(
        gameObject: GameObject<PIXI.Container>
    ): void {
        RenderSystem.driver.removeGameObject(gameObject);
    }

    public init(options?: any): void {
        RenderSystem.driver.init(this.scene.id, this.scene.index);

        this.scene.on(SceneEvent.GameObjectAdded, RenderSystem.gameObjectAdded);
        this.scene.on(
            SceneEvent.GameObjectRemoved,
            RenderSystem.gameObjectRemoved
        );
        this.scene.on(
            RenderSystemEvent.BRING_TO_FRONT,
            RenderSystem.onBringToFront
        );
        this.scene.on(
            RenderSystemEvent.SORT_CHILDREN,
            RenderSystem.sortChildren
        );
    }

    public onStart(): void {}

    public onStop(): void {}

    public onDestroy(): void {
        this.scene.off(
            SceneEvent.GameObjectAdded,
            RenderSystem.gameObjectAdded
        );
        this.scene.off(
            SceneEvent.GameObjectRemoved,
            RenderSystem.gameObjectRemoved
        );
        this.scene.off(
            RenderSystemEvent.BRING_TO_FRONT,
            RenderSystem.onBringToFront
        );
        this.scene.off(
            RenderSystemEvent.SORT_CHILDREN,
            RenderSystem.sortChildren
        );

        // window.removeEventListener('resize', this.onResize);
        // window.removeEventListener('orientationchange', this.onResize);
    }

    public onLateUpdate(): void {}

    public handleStateChanged(state: IGameState): void {}

    public onUpdate(): void {}

    public async preload(): Promise<any> {
        return undefined;
    }
}
